import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {environment} from '../../../environments/environment';
import {WaitlistPortalPageDto} from './dtos/waitlist-portal-view.dto';

/**
 * The patient's side of the waitlist. Every call is anonymous and addressed by the `reference` from
 * the patient's link; the backend forwards it to the practice's server that owns the entry. Each call
 * answers with the refreshed view.
 */
@Injectable({
  providedIn: 'root'
})
export class WaitlistService {
  http: HttpClient = inject(HttpClient);

  private baseUrl(reference: string): string {
    return `${environment.cenplexBookingApiBaseUrl}/waitlist/portal/${encodeURIComponent(reference)}`;
  }

  loadEntry(reference: string): Observable<WaitlistPortalPageDto> {
    return this.http.get<WaitlistPortalPageDto>(this.baseUrl(reference));
  }

  // also settles a reservation held for the patient, even when they booked a different slot
  bookSlot(reference: string, employeeId: number, from: string, to: string): Observable<WaitlistPortalPageDto> {
    return this.http.post<WaitlistPortalPageDto>(`${this.baseUrl(reference)}/book`, {employeeId, from, to});
  }

  // "this time never works": the slot and anything overlapping it are not suggested again. Not used by
  // a screen yet.
  rejectSlot(reference: string, employeeId: number, from: string, to: string): Observable<WaitlistPortalPageDto> {
    return this.http.post<WaitlistPortalPageDto>(`${this.baseUrl(reference)}/reject`, {employeeId, from, to});
  }

  // "don't hold this for me": releases the reservation, and the slot stays bookable for everyone
  declineReservation(reference: string): Observable<WaitlistPortalPageDto> {
    return this.http.post<WaitlistPortalPageDto>(`${this.baseUrl(reference)}/reservation/decline`, {});
  }

  cancel(reference: string, reason?: string): Observable<WaitlistPortalPageDto> {
    return this.http.post<WaitlistPortalPageDto>(`${this.baseUrl(reference)}/cancel`, {reason});
  }

  // mutes or unmutes the availability mails; the patient stays on the waitlist either way
  setNotifications(reference: string, enabled: boolean): Observable<WaitlistPortalPageDto> {
    return this.http.post<WaitlistPortalPageDto>(`${this.baseUrl(reference)}/notifications`, {enabled});
  }
}
